import { applyPseudoRules, createDecorScreen, type Decoration, pseudoRulesOf, sanitizeDecoration } from "@innoclouds/ui";
import { useEventListener } from "@vueuse/core";
import i18next from "i18next";
import { defineStore } from "pinia";
import { computed, shallowRef, watch } from "vue";
import { previewDecoration } from "#/preview";
import { namedFontFamilies } from "#/utils/fonts";
import { useMainStore } from "./main";

export type {
	Decoration,
	DecorationBinding,
	DecorationCss,
	DecorationElement,
	DecorationIcon,
	DecorationStates,
	DecorScreen,
} from "@innoclouds/ui";

export const DECORATION_KEY = "decoration";

const readStored = () => {
	try {
		return localStorage.getItem(DECORATION_KEY);
	} catch {
		return null;
	}
};

const parse = (raw: string | null): Decoration | null => {
	if (!raw) return null;

	try {
		return sanitizeDecoration(JSON.parse(raw));
	} catch (error) {
		console.error("[decoration]", error);
		return null;
	}
};

export const useDecorationStore = defineStore("decoration", () => {
	let raw = readStored();
	const stored = shallowRef(parse(raw));
	const data = computed(() => (previewDecoration.value === undefined ? stored.value : sanitizeDecoration(previewDecoration.value)));

	watch(data, (decoration) => applyPseudoRules(pseudoRulesOf(decoration)), { immediate: true });

	const refresh = () => {
		const next = readStored();
		if (next === raw) return;

		raw = next;
		stored.value = parse(next);
	};

	useEventListener("storage", (event) => {
		if (event.key === null || event.key === DECORATION_KEY) refresh();
	});
	useEventListener("focus", refresh);

	const mainStore = useMainStore();
	const language = shallowRef(i18next.language);
	i18next.on("languageChanged", (next) => {
		language.value = next;
	});
	const languages = computed(() => [language.value, mainStore.config_lib?.default_language].filter((code): code is string => !!code));

	const screen = (screenName?: string | null) =>
		createDecorScreen(
			() => data.value,
			screenName,
			() => namedFontFamilies.value,
			() => languages.value,
		);

	return { data, refresh, screen };
});
